import {type ComponentType, type ReactNode} from 'react';

import {createView, view} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';

import {AppConfig, AppError} from '../../../core/types';
import {ComponentWithOverride} from '../../../core/components/ComponentWithOverride';
import {ErrorScreen} from '../../../core/components/ErrorScreen';
import {ThreadsConfirmationModal} from './ThreadsConfirmationModal';
import {ThreadsConversationScreenContent} from './ThreadsConversationScreenContent';
import {ThreadsConversationScreenFooter} from './ThreadsConversationScreenFooter';
import {ThreadsConversationScreenHeader} from './ThreadsConversationScreenHeader';
import {ThreadsScreen} from '../ThreadsScreen';
import {
  useThreadsConfig,
  useThreadsConversationViewModelState,
} from '../../app/hooks';
import {useTitlePolling} from '../../hooks/useTitlePolling';

type Props = {
  header?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  content?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  log?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  input?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  footer?: ReactNode | ComponentType<{conversation?: Conversation | null}>;
  errorScreen?: ReactNode | ComponentType<{config: AppConfig; error: AppError}>;
};

export const ThreadsConversationScreen = createView(
  ({header, content, footer, errorScreen, log, input, ...props}: Props) => {
    const config = useThreadsConfig();

    const {conversation, error} = useThreadsConversationViewModelState();

    useTitlePolling();

    if (error) {
      return (
        <ComponentWithOverride<{config: AppConfig; error: AppError}>
          override={errorScreen}
          component={ErrorScreen}
          config={config}
          error={error}
        />
      );
    }

    return (
      <view.div data-testid="threads-conversation-screen" {...props}>
        <ThreadsScreen
          header={
            <ComponentWithOverride<{conversation?: Conversation | null}>
              override={header}
              component={ThreadsConversationScreenHeader}
              conversation={conversation}
            />
          }
          content={
            <ComponentWithOverride<{
              conversation?: Conversation | null;
            }>
              override={content}
              component={ThreadsConversationScreenContent}
              conversation={conversation}
            />
          }
          footer={
            <ComponentWithOverride<{conversation?: Conversation | null}>
              override={footer}
              component={ThreadsConversationScreenFooter}
              conversation={conversation}
            />
          }
        />
        <ThreadsConfirmationModal />
      </view.div>
    );
  },
);
